import * as React from 'react'
import { Plus, ShieldCheck } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Notice } from '@/components/ui/notice'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { ServerDataTable, type TableRow, type TableState } from '@/components/visitone/server-data-table'
import { RefusedSurface } from '@/components/visitone/surfaces/access-surface'
import { ChangeRolesPanel } from '@/components/visitone/surfaces/people/change-roles-panel'
import { InvitePersonPanel } from '@/components/visitone/surfaces/people/invite-person-panel'
import { PeopleFrame } from '@/components/visitone/surfaces/people/people-shared'
import {
  RemoveAccessModal,
  SendAgainModal,
  WithdrawInvitationModal,
} from '@/components/visitone/surfaces/people/people-modals'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { useInvitations, usePeopleUsers } from '@/hooks/use-people'
import { ORGANISATION_ADMIN, PeopleRequestError, type Invitation, type Person } from '@/lib/bff/people'
import { formatTenantDate, formatTenantDateTime } from '@/lib/format/tenant-date'
import { focusWithin, personName, roleLabel } from '@/lib/people/people-text'

/** Router state that opens Invite a person, e.g. from the command palette. */
export type PeopleRouteState = { openInvite?: boolean }

/* The latest action result. Only one is shown; each new result replaces the last. */
type Result =
  | { kind: 'sent'; email: string }
  | { kind: 'withdrawn'; email: string }
  | { kind: 'sentAgain'; email: string }
  | { kind: 'rolesSaved'; name: string }
  | { kind: 'removed'; name: string }

const INVITATIONS_HEADING_ID = 'people-invitations-title'
const sendAgainButtonId = (invitationId: string) => `send-again-${invitationId}`

/*
 * Setup › People & access › Users (VIS-332): the tenant's venue users and
 * their roles, then — for a caller holding `access.manage` — the invitations
 * not yet accepted. The route renders this only with `users.view`. The BFF
 * decides per person whether their roles can be changed and whether their
 * access can be removed; what the caller can't do is not offered.
 */
export function PeopleUsersSurface() {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()
  const { can, tenantName: tenant } = useAccess()
  const canManage = can('access.manage')
  const actingAsLandlord = can('landlords.manage')
  const rootRef = React.useRef<HTMLDivElement>(null)
  const inviteButtonId = React.useId()

  const [page, setPage] = React.useState(0)
  const users = usePeopleUsers(page, true)
  const people = React.useMemo(() => users.page?.data ?? [], [users.page])
  const [invitationPage, setInvitationPage] = React.useState(0)
  const invitations = useInvitations(invitationPage, canManage)
  const invited = React.useMemo(() => invitations.page?.data ?? [], [invitations.page])

  const [result, setResult] = React.useState<Result | null>(null)
  const [inviteOpen, setInviteOpen] = React.useState(false)
  const [inviteKey, setInviteKey] = React.useState(0)
  const [changing, setChanging] = React.useState<Person | null>(null)
  const [removing, setRemoving] = React.useState<Person | null>(null)
  const [withdrawing, setWithdrawing] = React.useState<Invitation | null>(null)
  const [sendingAgain, setSendingAgain] = React.useState<Invitation | null>(null)
  const [focusInvitation, setFocusInvitation] = React.useState<string | null>(null)

  const openInvite = React.useCallback(() => {
    setInviteKey((key) => key + 1)
    setInviteOpen(true)
  }, [])

  const openFromRoute = canManage && (location.state as PeopleRouteState | null)?.openInvite === true
  React.useEffect(() => {
    if (!openFromRoute) return
    openInvite()
    navigate(location.pathname, { replace: true, state: null })
  }, [openFromRoute, openInvite, navigate, location.pathname])

  // A page emptied by changes elsewhere falls back to the last page that exists.
  const emptyLaterPage = page > 0 && users.page !== undefined && people.length === 0
  React.useEffect(() => {
    if (emptyLaterPage) setPage(Math.max(0, (users.page?.pagination.totalPages ?? 1) - 1))
  }, [emptyLaterPage, users.page])
  const emptyLaterInvitations = invitationPage > 0 && invitations.page !== undefined && invited.length === 0
  React.useEffect(() => {
    if (emptyLaterInvitations) setInvitationPage(Math.max(0, (invitations.page?.pagination.totalPages ?? 1) - 1))
  }, [emptyLaterInvitations, invitations.page])

  // After Send again the row is the new invitation; focus its Send again once it is listed.
  React.useEffect(() => {
    if (!focusInvitation) return
    const button = document.getElementById(sendAgainButtonId(focusInvitation))
    if (!button) return
    button.focus()
    setFocusInvitation(null)
  }, [focusInvitation, invited])

  const usersForbidden = users.error instanceof PeopleRequestError && users.error.status === 403
  if (usersForbidden) return <RefusedSurface />

  let state: TableState
  if (users.error) state = 'error'
  else if (!users.page || emptyLaterPage) state = 'loading'
  else if (people.length === 0) state = 'empty'
  else state = 'data'

  const organisationAdmins = people.filter((person) => person.roles.some((role) => role.id === ORGANISATION_ADMIN))
  const otherOrganisationAdmin = (self: Person) => {
    const other = organisationAdmins.find((person) => person.userId !== self.userId)
    return other ? personName(other) : null
  }

  const byId = new Map(people.map((person) => [person.userId, person]))
  const rows: TableRow[] = people.map((person) => ({
    id: person.userId,
    label: personName(person),
    cells: {
      person: {
        primary: personName(person),
        secondary: person.name && person.email ? person.email : undefined,
        pills: person.isSelf ? [{ tone: 'neutral' as const, label: t('people.row.you') }] : undefined,
      },
      roles:
        person.roles.length > 0
          ? { primary: '', pills: person.roles.map((role) => ({ tone: 'neutral' as const, label: roleLabel(t, role) })) }
          : { primary: t('people.row.noRole') },
    },
  }))

  const renderUserActions = (row: TableRow) => {
    const person = byId.get(row.id)
    if (!person) return null
    const name = personName(person)
    return (
      <>
        {person.isSelf ? (
          <span className="text-xs text-fg-muted">{t('people.row.cannotChangeSelf')}</span>
        ) : person.canChangeRoles ? (
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('people.row.changeRolesName', { name })}
            onClick={() => setChanging(person)}
          >
            {t('people.row.changeRoles')}
          </Button>
        ) : null}
        {person.removal === 'allowed' || person.removal === 'self' ? (
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('people.row.removeName', { name })}
            onClick={() => setRemoving(person)}
          >
            {t('people.row.remove')}
          </Button>
        ) : person.removal === 'onlyOrganisationAdmin' ? (
          <span className="text-xs text-fg-muted">{t('people.row.onlyOrgAdmin')}</span>
        ) : null}
      </>
    )
  }

  const pagination = users.page?.pagination
  const inviteAction = canManage ? (
    <Button id={inviteButtonId} variant="primary" onClick={openInvite}>
      <Plus />
      {t('people.users.invite')}
    </Button>
  ) : undefined

  return (
    <PeopleFrame
      rootRef={rootRef}
      pageTitle={t('people.users.title')}
      summary={t('people.users.summary', { tenant })}
      action={inviteAction}
    >
      {actingAsLandlord ? (
        <Notice tone="info" icon={<ShieldCheck className="size-5" />} title={t('people.actingNotice.title')}>
          {t('people.actingNotice.body', { tenant })}
        </Notice>
      ) : null}

      <ResultMessage result={result} tenant={tenant} />

      <ServerDataTable
        layout="stacked"
        state={state}
        onRetry={() => void users.revalidate()}
        columns={[
          { key: 'person', label: t('people.users.column') },
          { key: 'roles', label: t('people.users.rolesColumn', { tenant }) },
        ]}
        rows={rows}
        sortedBy={{ key: 'person', direction: 'asc' }}
        rowActions={{ label: t('people.users.actions'), render: renderUserActions }}
        caption={t('people.users.caption', { tenant })}
        regionLabel={t('people.users.caption', { tenant })}
        footer={
          state === 'loading'
            ? t('people.users.loading')
            : t('people.users.count', { count: pagination?.totalCount ?? people.length })
        }
        pagination={pagination ? { page: pagination.page, totalPages: pagination.totalPages, onPageChange: setPage } : undefined}
        loadingRowCount={4}
        messages={{
          loadErrorTitle: t('people.users.loadFailed.title'),
          loadErrorBody: t('people.users.loadFailed.body'),
          retry: t('people.users.loadFailed.retry'),
        }}
        empty={{
          title: t('people.users.empty.title', { tenant }),
          body: t('people.users.empty.body', { tenant }),
          action: canManage ? (
            <Button variant="secondary" onClick={openInvite}>
              {t('people.users.invite')}
            </Button>
          ) : undefined,
        }}
      />

      {canManage ? (
        <InvitationsSection
          invitations={invited}
          error={Boolean(invitations.error)}
          loading={!invitations.page || emptyLaterInvitations}
          pagination={invitations.page?.pagination}
          timeZone={invitations.page?.timeZone}
          onPageChange={setInvitationPage}
          onRetry={() => void invitations.revalidate()}
          onWithdraw={setWithdrawing}
          onSendAgain={setSendingAgain}
        />
      ) : null}

      <InvitePersonPanel
        key={inviteKey}
        open={inviteOpen}
        memberEmails={new Set(people.flatMap((person) => (person.email ? [person.email.toLowerCase()] : [])))}
        onClose={() => setInviteOpen(false)}
        onSent={(invitation) => {
          setResult({ kind: 'sent', email: invitation.email })
          setInviteOpen(false)
        }}
      />

      <ChangeRolesPanel
        person={changing}
        onClose={() => setChanging(null)}
        onSaved={(person) => {
          setResult({ kind: 'rolesSaved', name: personName(person) })
          setChanging(null)
        }}
      />

      <RemoveAccessModal
        person={removing}
        otherOrganisationAdmin={removing ? otherOrganisationAdmin(removing) : null}
        onClose={() => setRemoving(null)}
        onRemoved={(person) => {
          setResult({ kind: 'removed', name: personName(person) })
          setRemoving(null)
          focusWithin(rootRef.current, 'h1')
        }}
      />

      <WithdrawInvitationModal
        invitation={withdrawing}
        onClose={() => setWithdrawing(null)}
        onWithdrawn={(invitation) => {
          setResult({ kind: 'withdrawn', email: invitation.email })
          setWithdrawing(null)
          focusWithin(rootRef.current, `#${INVITATIONS_HEADING_ID}`)
        }}
      />

      <SendAgainModal
        invitation={sendingAgain}
        onClose={() => setSendingAgain(null)}
        onSent={(previous, replacement) => {
          setResult({ kind: 'sentAgain', email: previous.email })
          setSendingAgain(null)
          setFocusInvitation(replacement.id)
        }}
      />
    </PeopleFrame>
  )
}

function InvitationsSection({
  invitations,
  error,
  loading,
  pagination,
  timeZone,
  onPageChange,
  onRetry,
  onWithdraw,
  onSendAgain,
}: {
  invitations: Invitation[]
  error: boolean
  loading: boolean
  pagination?: { page: number; totalPages: number; totalCount: number }
  timeZone?: string
  onPageChange: (page: number) => void
  onRetry: () => void
  onWithdraw: (invitation: Invitation) => void
  onSendAgain: (invitation: Invitation) => void
}) {
  const t = useT()
  const { tenantName: tenant } = useAccess()

  let state: TableState
  if (error) state = 'error'
  else if (loading) state = 'loading'
  else if (invitations.length === 0) state = 'empty'
  else state = 'data'

  const byId = new Map(invitations.map((invitation) => [invitation.id, invitation]))
  const rows: TableRow[] = invitations.map((invitation) => {
    const usable = invitation.status === 'usable'
    const date = formatTenantDateTime(invitation.validUntil, timeZone)
    return {
      id: invitation.id,
      label: invitation.email,
      cells: {
        person: {
          primary: invitation.name ?? invitation.email,
          secondary: [
            invitation.name ? invitation.email : null,
            t('people.invitations.invitedBy', {
              name: invitation.sentByName,
              date: formatTenantDate(invitation.sentAt, timeZone),
            }),
          ]
            .filter(Boolean)
            .join(' · '),
        },
        roles: { primary: '', pills: invitation.roles.map((role) => ({ tone: 'neutral' as const, label: roleLabel(t, role) })) },
        status: {
          primary: '',
          pills: [
            usable
              ? { tone: 'success' as const, label: t('people.invitations.usable') }
              : { tone: 'neutral' as const, label: t('people.invitations.unusable') },
          ],
          secondary: usable
            ? t('people.invitations.usableUntil', { date })
            : t('people.invitations.unusableSince', { date }),
        },
      },
    }
  })

  const renderActions = (row: TableRow) => {
    const invitation = byId.get(row.id)
    if (!invitation) return null
    return (
      <>
        <Button
          id={sendAgainButtonId(invitation.id)}
          variant="secondary"
          size="sm"
          aria-label={t('people.row.sendAgainName', { email: invitation.email })}
          onClick={() => onSendAgain(invitation)}
        >
          {t('people.row.sendAgain')}
        </Button>
        {invitation.status === 'usable' ? (
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('people.row.withdrawName', { email: invitation.email })}
            onClick={() => onWithdraw(invitation)}
          >
            {t('people.row.withdraw')}
          </Button>
        ) : null}
      </>
    )
  }

  return (
    <section aria-labelledby={INVITATIONS_HEADING_ID} className="mt-4 flex flex-col gap-3">
      <div>
        <h2 id={INVITATIONS_HEADING_ID} className="text-lg font-semibold text-fg-strong outline-none">
          {t('people.invitations.title')}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">{t('people.invitations.summary', { tenant })}</p>
      </div>
      <ServerDataTable
        layout="stacked"
        state={state}
        onRetry={onRetry}
        columns={[
          { key: 'person', label: t('people.invitations.column') },
          { key: 'roles', label: t('people.invitations.rolesColumn') },
          { key: 'status', label: t('people.invitations.statusColumn') },
        ]}
        rows={rows}
        rowActions={{ label: t('people.invitations.actions'), render: renderActions }}
        caption={t('people.invitations.caption', { tenant })}
        regionLabel={t('people.invitations.caption', { tenant })}
        footer={
          state === 'loading'
            ? t('people.invitations.loading')
            : t('people.invitations.count', { count: pagination?.totalCount ?? invitations.length })
        }
        pagination={pagination ? { page: pagination.page, totalPages: pagination.totalPages, onPageChange } : undefined}
        loadingRowCount={2}
        messages={{
          loadErrorTitle: t('people.invitations.loadFailed.title'),
          loadErrorBody: t('people.invitations.loadFailed.body'),
          retry: t('people.users.loadFailed.retry'),
        }}
        empty={{ title: t('people.invitations.empty') }}
      />
    </section>
  )
}

function ResultMessage({ result, tenant }: { result: Result | null; tenant: string }) {
  const t = useT()
  if (!result) return null
  switch (result.kind) {
    case 'sent':
      return <OperationFeedback state="success" title={t('people.invite.sent', { email: result.email })} />
    case 'withdrawn':
      return <OperationFeedback state="success" title={t('people.withdraw.done', { email: result.email })} />
    case 'sentAgain':
      return <OperationFeedback state="success" title={t('people.sendAgain.done', { email: result.email })} />
    case 'rolesSaved':
      return <OperationFeedback state="success" title={t('people.roles.saved', { name: result.name })} />
    case 'removed':
      return <OperationFeedback state="success" title={t('people.remove.done', { name: result.name, tenant })} />
  }
}
